import { createRef, useState } from "react";
import styled from 'styled-components/macro';
import classNames from 'classnames';
import { PinchPanImg, PinchPan } from "./utils/pinch-pan-helpers";
import HotspotFlag, { HotspotFlagElement, PinWrapper, StyledTechSectorButton, TechSectorList } from "./ui/hotspot-flag";
import { getOffsetPosition } from "./utils/position";
import { useStore } from "./store";
import { useHistory, useParams } from "react-router-dom";
import { HotspotList, HotspotConfig } from "../config/city";
import { CSSTransition } from "react-transition-group";
import { BuildingType } from "../config/buildings";
import BuildingNavigation from "./ui/building-navigation";
import { RouteParams } from "../interfaces/router";
import { BuildingTechSectors } from "../interfaces/buildings";
import useTranslations from './utils/language-provider';
import { isMobileDevice } from "./utils/mobile-detection";
import Breadcrumbs, { BreadcrumbsWrapper, ButtonsWrapper, StyledContactButton } from "./ui/breadcrumbs";
import FullscreenToggleButton from "./ui/fullscreen-button";
import { ReactComponent as Contact } from '../assets/icon-contact.svg';

type BuildingsProps = {
    visible: boolean;
    pageBackBtn?: boolean;
    pageTitle?: string;
    pageSubTitle?: string;
    imgRef?: React.RefObject<HTMLImageElement>;
    onImgLoad?(): void;
    src: string;
    hotspots?: HotspotList;
    techSectors?: BuildingTechSectors[];
    transitionClassName?: string;
    type?: BuildingType;
    isCity?: boolean;
}

const StyledHotspotFlag = styled(HotspotFlag)`
    --animate-duration: 700ms;
    opacity: 0;

    &.active {
        opacity: 1;
    }
`;

const StyledBigHotspotFlag = styled(HotspotFlag)`
    opacity: 1;
    width: 60%;
    height: 25%;
    z-index: ${(props) => props.theme.zIndex.popover};

    ${HotspotFlagElement}{
        width: 100%;
        height: 100%;
        position: static;

        > * {
            position: static;
        }
        .inner {
            height: 40px;
            clip-path: none;
            align-items: center;
        }
        &:after {
            display: none;
        }
    }
    ${PinWrapper} {
        display: none;
    }
    ${TechSectorList} {
        position: static;

        svg {
            bottom: 0;
        }
    }
    ${StyledTechSectorButton} {
        opacity: 1;
        top: 0;
        height: 40px;
        pointer-events: auto;
    }
`;

const BigHotspotBackdrop = styled.div<{size: {width: number, height: number}}>`
    position: absolute;
    top: 0;
    left: 0;
    width: ${props => props.size.width}px;
    height: ${props => props.size.height}px;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${props => props.theme.zIndex.overlay};
`;

const ZoomContainer = styled.div`
    position: relative;
    overflow: hidden;
`

const Buildings = ({
    visible,
    imgRef,
    src,
    pageTitle,
    hotspots,
    techSectors,
    transitionClassName = 'page-zoom',
    onImgLoad = () => {},
    type,
    isCity
}: BuildingsProps) => {
    const {state} = useStore();
    const history = useHistory();
    const translations = useTranslations();
    const [hotspotsActive, setHotspotsActive] = useState(false);
    const [bigHotspotVisible, setBigHotspotVisible] = useState(false);
    const [currentHotspot, setCurrentHotspot] = useState<HotspotConfig | null>(null);
    const containerRef = createRef<HTMLDivElement>();
    const {techSector: techSectorParam = ''} = useParams<RouteParams>();

    const hasSubbildings = techSectors?.length;
    let spots = hotspots;
    let img = src;

    if (hasSubbildings) {
        const sectorDetails = techSectors?.find(sector => sector.type === techSectorParam);

        if (sectorDetails) {
            spots = sectorDetails.buildings;
            img = sectorDetails.img || '';
        }
    }

    const handleImgLoad = () => {
        onImgLoad();
        setHotspotsActive(true);
    }

    const openBuilding = (path: string, coords: string) => {
        if (containerRef.current) {
            containerRef.current.style.transformOrigin = coords
        }
        history.push(`/${path}`);
    }

    const handleComeback = () => {
        setHotspotsActive(true);
    }

    const handleHotspotClick = (config: HotspotConfig, pos: {left: string, top: string}) => {
        if (config.type !== 'inactive') {
            openBuilding(config.link || config.building, `${pos.left} ${pos.top}`)
        }
    }

    const handleTooltipClick = (hotspotconfig: HotspotConfig) => {
        if (isMobileDevice()) {
            setCurrentHotspot(hotspotconfig);
            setBigHotspotVisible(true);
        } else {
            handleHotspotClick(hotspotconfig, getOffsetPosition({
                ...hotspotconfig.coordinates,
                imgWidth: state.appDimensions.width,
                imgHeight: state.appDimensions.height
            }));
        }
    }

    return (
        <div>
            <ZoomContainer>
                {type && (
                    <>
                        <BreadcrumbsWrapper>
                            <Breadcrumbs
                                building={{
                                    label: translations.buildings[type].name,
                                    href: `/${type}`,
                                }}
                                subBuilding={undefined}
                                techSektor={
                                    techSectorParam
                                        ? {
                                              label: translations.sectors[techSectorParam].name,
                                              href: `/${type}/${techSectorParam}`,
                                          }
                                        : undefined
                                }
                            />
                            <ButtonsWrapper>
                                {isMobileDevice() && <FullscreenToggleButton />}
                                <StyledContactButton href={translations.contact?.href || '/contact'}>
                                    <Contact />
                                    <p>{translations.contact?.text || 'Kontakt'}</p>
                                </StyledContactButton>
                            </ButtonsWrapper>
                        </BreadcrumbsWrapper>
                        {hasSubbildings && <BuildingNavigation buildingType={type} />}
                    </>
                )}
                {isCity && (
                    <ButtonsWrapper isCity>
                        {isMobileDevice() && <FullscreenToggleButton />}
                        <StyledContactButton href={translations.contact?.href || '/contact'}>
                            <Contact />
                            <p>{translations.contact?.text || 'Kontakt'}</p>
                        </StyledContactButton>
                    </ButtonsWrapper>
                )}

                <CSSTransition
                    in={visible}
                    timeout={500}
                    classNames={transitionClassName}
                    nodeRef={containerRef}
                    onEntered={() => handleComeback()}
                    onExited={() => setHotspotsActive(false)}
                >
                    <div ref={containerRef}>
                        <PinchPan layer={1}>
                            <PinchPanImg forwardedRef={imgRef} src={img} onLoad={handleImgLoad} />

                            {hotspotsActive &&
                                spots?.map((hotspotConfig, index) => {
                                    const pos = getOffsetPosition({
                                        ...hotspotConfig.coordinates,
                                        imgWidth: state.appDimensions.width,
                                        imgHeight: state.appDimensions.height,
                                        imgScale: 1,
                                        imgOffsetX: 0,
                                        imgOffsetY: 0,
                                    });

                                    if (hotspotConfig.component) {
                                        return (
                                            <hotspotConfig.component
                                                key={hotspotConfig.building}
                                                style={{
                                                    ...pos,
                                                    animationDelay: `${index * 100}ms`,
                                                }}
                                                className={classNames('animated', {
                                                    fadeIn: hotspotsActive,
                                                    active: hotspotsActive,
                                                })}
                                            />
                                        );
                                    }

                                    return (
                                        <StyledHotspotFlag
                                            config={hotspotConfig}
                                            key={hotspotConfig.building}
                                            style={{
                                                ...pos,
                                                animationDelay: `${index * 100}ms`,
                                            }}
                                            className={classNames('animated', {
                                                fadeIn: hotspotsActive,
                                                active: hotspotsActive,
                                            })}
                                            onClickTooltip={() => handleTooltipClick(hotspotConfig)}
                                        />
                                    );
                                })}
                        </PinchPan>
                    </div>
                </CSSTransition>
                {bigHotspotVisible && currentHotspot !== null && (
                    <BigHotspotBackdrop onClick={() => setBigHotspotVisible(false)} size={state.appDimensions}>
                        <StyledBigHotspotFlag
                            config={{ ...currentHotspot, isBigFlag: true }}
                            key={currentHotspot?.building}
                            className={classNames('animated', {
                                fadeIn: hotspotsActive,
                                active: hotspotsActive,
                            })}
                            onClickTooltip={() =>
                                handleHotspotClick(
                                    currentHotspot,
                                    getOffsetPosition({
                                        ...currentHotspot.coordinates,
                                        imgWidth: state.appDimensions.width,
                                        imgHeight: state.appDimensions.height,
                                    }),
                                )
                            }
                        />
                    </BigHotspotBackdrop>
                )}
            </ZoomContainer>
        </div>
    );
}

export default Buildings;
